import { useEffect, useState, useCallback } from "react";
import { api } from "@/api/client";
import { useAuth } from "@/store/auth";
import { fmtDate, StatusBadge, PriorityBadge } from "@/components/common";
import { toast } from "sonner";
import { Plus, Search, X, Loader2, ChevronRight, AlertCircle, X as XIcon } from "lucide-react";

const STATUS_CHIPS = [
  { key: "", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "in_progress", label: "In Progress" },
  { key: "delayed", label: "Delayed" },
  { key: "awaiting_approval", label: "Approval" },
  { key: "completed", label: "Completed" },
  { key: "closed", label: "Closed" },
];

export default function MobileTasks() {
  const { hasPerm } = useAuth();
  const [tab, setTab] = useState("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({});
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (tab) params.tab = tab;
      if (search) params.search = search;
      const r = await api.get("/tasks", { params });
      setRows(r.data?.data || []);
      setMeta(r.data?.meta || {});
    } finally { setLoading(false); }
  }, [tab, search, page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="flex flex-col h-full">
      {/* Sticky search + chips */}
      <div className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur px-4 pt-3 pb-2 space-y-2">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search tasks..."
            className="w-full pl-9 pr-8 py-2.5 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:border-[#334E7B] focus:ring-2 focus:ring-[#334E7B]/20 transition-all"
          />
          {search && (
            <button onClick={() => { setSearch(""); setPage(1); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
              <X size={14} />
            </button>
          )}
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {STATUS_CHIPS.map(c => (
            <button key={c.key || "all"}
              onClick={() => { setTab(c.key); setPage(1); }}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                tab === c.key
                  ? "bg-[#334E7B] text-white"
                  : "bg-white text-slate-600 border border-slate-200"
              }`}>
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* Task count */}
      <div className="px-4 py-1.5 text-xs text-slate-400 font-medium">{meta?.total ?? 0} tasks</div>

      {/* Task list */}
      <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2.5">
        {loading ? (
          <div className="flex justify-center pt-8"><Loader2 className="animate-spin text-slate-400" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center pt-12 text-slate-400">
            <AlertCircle size={32} className="mx-auto mb-2 opacity-40" />
            <div className="text-sm">No tasks found</div>
          </div>
        ) : (
          rows.map(t => (
            <button key={t.id}
              onClick={() => setSelectedTaskId(t.id)}
              className="w-full bg-white rounded-2xl p-4 text-left border border-slate-100 active:bg-slate-50 transition-colors">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <span className="font-mono text-[10px] text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded">{t.task_code}</span>
                    <StatusBadge status={t.status} />
                    {t.is_overdue && (
                      <span className="text-[9px] font-bold text-red-600 uppercase tracking-wider bg-red-50 px-1.5 py-0.5 rounded">Overdue</span>
                    )}
                  </div>
                  <div className="text-sm font-semibold text-slate-900 leading-snug line-clamp-2">{t.title}</div>
                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                    <span>{t.assigned_to_name || "Unassigned"}</span>
                    {t.original_due_date && (
                      <span className={t.is_overdue ? "text-red-600 font-medium" : ""}>
                        · Due {fmtDate(t.original_due_date)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  <PriorityBadge priority={t.priority_name} />
                  <ChevronRight size={16} className="text-slate-300" />
                </div>
              </div>
            </button>
          ))
        )}

        {/* Pagination */}
        {meta?.total_pages > 1 && (
          <div className="flex gap-2 pt-2">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
              className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-medium disabled:opacity-40 bg-white">Prev</button>
            <span className="flex-shrink-0 flex items-center text-xs text-slate-500 px-2">{page} / {meta.total_pages}</span>
            <button disabled={page >= meta.total_pages} onClick={() => setPage(p => p + 1)}
              className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-medium disabled:opacity-40 bg-white">Next</button>
          </div>
        )}
      </div>

      {/* FAB - New Task */}
      {hasPerm("create_task") && (
        <button
          onClick={() => setCreateOpen(true)}
          className="fixed bottom-20 right-4 w-14 h-14 bg-[#334E7B] text-white rounded-full shadow-lg flex items-center justify-center active:scale-95 transition-transform z-20"
          style={{ bottom: "calc(64px + env(safe-area-inset-bottom) + 12px)" }}>
          <Plus size={24} strokeWidth={2.5} />
        </button>
      )}

      {/* Task detail — slide-up bottom sheet */}
      {selectedTaskId && (
        <MobileTaskDetailSheet
          taskId={selectedTaskId}
          onClose={() => { setSelectedTaskId(null); load(); }}
        />
      )}

      {/* Create task */}
      {createOpen && (
        <MobileCreateSheet onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); load(); }} />
      )}
    </div>
  );
}

// ── Task Detail bottom sheet ────────────────────────────────

function MobileTaskDetailSheet({ taskId, onClose }) {
  const { hasPerm, user } = useAuth();
  const [task, setTask] = useState(null);
  const [timeline, setTimeline] = useState({ status_history: [] });
  const [remarks, setRemarks] = useState("");
  const [acting, setActing] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);

  const load = useCallback(async () => {
    try {
      const [t, tl] = await Promise.all([
        api.get(`/tasks/${taskId}`),
        api.get(`/tasks/${taskId}/timeline`),
      ]);
      setTask(t.data?.data);
      setTimeline(tl.data?.data || { status_history: [] });
    } catch {}
  }, [taskId]);

  useEffect(() => { load(); }, [load]);

  const doAction = async (action, label) => {
    setActing(true);
    try {
      await api.post(`/tasks/${taskId}/${action}`, { remarks });
      toast.success(`${label}`);
      setRemarks("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.message || "Failed"); }
    finally { setActing(false); }
  };

  const isAssignee = task?.assigned_to === user?.id;

  return (
    <div className="fixed inset-0 z-50 flex flex-col">
      <div className="flex-1 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="bg-white rounded-t-3xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-slate-200 rounded-full" />
        </div>
        <div className="flex items-center justify-between px-4 pb-3">
          <div className="font-heading font-bold text-base text-slate-900">Task Detail</div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-slate-100"><XIcon size={18} /></button>
        </div>

        <div className="overflow-y-auto flex-1 px-4 pb-safe">
          {!task ? (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin text-slate-400" /></div>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span className="font-mono text-xs text-slate-400 bg-slate-50 px-2 py-0.5 rounded">{task.task_code}</span>
                <StatusBadge status={task.status} />
                <PriorityBadge priority={task.priority_name} />
                {task.is_overdue && <span className="text-[9px] font-bold text-red-600 uppercase bg-red-50 px-1.5 py-0.5 rounded">Overdue</span>}
              </div>
              <div className="text-base font-heading font-bold text-slate-900 mb-3 leading-snug">{task.title}</div>

              <div className="grid grid-cols-2 gap-2 mb-4">
                {[
                  ["Assignee", task.assigned_to_name],
                  ["Assigned By", task.assigned_by_name],
                  ["Due Date", fmtDate(task.original_due_date)],
                  ["SLA Due", fmtDate(task.sla_due_date)],
                  ["Source", task.source],
                  ["Completed", fmtDate(task.completed_at)],
                ].map(([label, value]) => (
                  <div key={label} className="bg-slate-50 rounded-xl p-2.5">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
                    <div className="text-xs font-medium text-slate-800 mt-0.5">{value || "—"}</div>
                  </div>
                ))}
              </div>

              {task.description && (
                <div className="mb-4 bg-slate-50 rounded-xl p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Description</div>
                  <div className="text-xs text-slate-700 leading-relaxed">{task.description}</div>
                </div>
              )}

              {/* Actions */}
              <div className="mb-4">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Remarks (optional)</div>
                <textarea value={remarks} onChange={e => setRemarks(e.target.value)}
                  placeholder="Add remarks..." rows={2}
                  className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-[#334E7B] resize-none" />
                <div className="flex flex-wrap gap-2 mt-2">
                  {(isAssignee || hasPerm("edit_task")) && task.status === "Assigned" && (
                    <button onClick={() => doAction("accept", "Accepted")} disabled={acting}
                      className="flex-1 py-2.5 bg-slate-100 text-slate-800 rounded-xl text-sm font-semibold active:scale-95">Accept</button>
                  )}
                  {(isAssignee || hasPerm("edit_task")) && ["Assigned", "Accepted"].includes(task.status) && (
                    <button onClick={() => doAction("start", "Started")} disabled={acting}
                      className="flex-1 py-2.5 bg-slate-100 text-slate-800 rounded-xl text-sm font-semibold active:scale-95">Start</button>
                  )}
                  {(isAssignee || hasPerm("edit_task")) && ["Accepted", "In Progress", "Rework Required"].includes(task.status) && (
                    <button onClick={() => doAction("complete", "Completed")} disabled={acting}
                      className="flex-1 py-2.5 bg-[#334E7B] text-white rounded-xl text-sm font-semibold active:scale-95">Mark Complete</button>
                  )}
                  {hasPerm("approve_task") && task.status === "Awaiting Approval" && (
                    <>
                      <button onClick={() => doAction("approve", "Approved")} disabled={acting}
                        className="flex-1 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-semibold active:scale-95">Approve</button>
                      <button onClick={() => doAction("rework", "Rework")} disabled={acting}
                        className="flex-1 py-2.5 bg-orange-500 text-white rounded-xl text-sm font-semibold active:scale-95">Rework</button>
                      <button onClick={() => doAction("reject", "Rejected")} disabled={acting}
                        className="flex-1 py-2.5 bg-red-600 text-white rounded-xl text-sm font-semibold active:scale-95">Reject</button>
                    </>
                  )}
                  {task.status === "Return Confirmation Pending" && (
                    <>
                      <button onClick={() => doAction("return-confirm", "Confirmed")} disabled={acting}
                        className="flex-1 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-semibold active:scale-95">Confirm Received</button>
                      <button onClick={() => doAction("return-reject", "Not Received")} disabled={acting}
                        className="flex-1 py-2.5 bg-red-600 text-white rounded-xl text-sm font-semibold active:scale-95">Not Received</button>
                    </>
                  )}
                </div>
              </div>

              {/* Timeline toggle */}
              <button onClick={() => setShowTimeline(v => !v)}
                className="w-full py-2.5 text-sm font-semibold text-slate-600 bg-slate-50 rounded-xl mb-2 flex items-center justify-center gap-1">
                {showTimeline ? "Hide" : "Show"} Timeline ({timeline.status_history.length} events)
              </button>
              {showTimeline && (
                <div className="space-y-2 mb-4">
                  {timeline.status_history.map((s, i) => (
                    <div key={s.id || i} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className="w-2.5 h-2.5 rounded-full bg-slate-900 mt-1 flex-shrink-0" />
                        {i < timeline.status_history.length - 1 && <div className="w-px flex-1 bg-slate-200 my-1" />}
                      </div>
                      <div className="pb-2">
                        <div className="text-xs font-semibold text-slate-800">
                          {s.old_status || "Created"} → {s.new_status}
                        </div>
                        <div className="text-[10px] text-slate-400">{s.changed_by_name} · {fmtDate(s.changed_at)}</div>
                        {s.remarks && <div className="text-[11px] text-slate-500 mt-0.5 italic">"{s.remarks}"</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Create Task bottom sheet ────────────────────────────────

function MobileCreateSheet({ onClose, onCreated }) {
  const [form, setForm] = useState({ title: "", assigned_to: "", original_due_date: "" });
  const [users, setUsers] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/users?limit=200").then(r => setUsers(r.data?.data || []));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.assigned_to) { toast.error("Title and assignee required"); return; }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (payload.original_due_date) payload.sla_due_date = new Date(payload.original_due_date).toISOString();
      else delete payload.original_due_date;
      await api.post("/tasks", payload);
      toast.success("Task created");
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.message || "Failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col">
      <div className="flex-1 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="bg-white rounded-t-3xl max-h-[85vh] flex flex-col overflow-hidden">
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 bg-slate-200 rounded-full" />
        </div>
        <div className="flex items-center justify-between px-4 pb-3 flex-shrink-0">
          <div className="font-heading font-bold text-base">New Task</div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-slate-100"><XIcon size={18} /></button>
        </div>
        <form onSubmit={submit} className="px-4 space-y-3 overflow-y-auto flex-1"
          style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 88px)" }}>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Title *</label>
            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              className="w-full px-3 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-[#334E7B]"
              placeholder="Task title..." required />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Assignee *</label>
            <select value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })}
              className="w-full px-3 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-[#334E7B]" required>
              <option value="">Select user...</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Due Date</label>
            <input type="datetime-local" value={form.original_due_date}
              onChange={e => setForm({ ...form, original_due_date: e.target.value })}
              className="w-full px-3 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-[#334E7B]" />
          </div>
          <button type="submit" disabled={submitting}
            className="w-full py-3 bg-[#334E7B] text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 active:scale-95 transition-transform">
            {submitting && <Loader2 size={16} className="animate-spin" />}
            Create Task
          </button>
        </form>
      </div>
    </div>
  );
}
